import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: number
  kind: ToastKind
  message: string
  /** Optional second line, e.g. what was inserted. */
  detail?: string
}

interface ToastContextValue {
  toasts: Toast[]
  /** Show a message. Returns the id so it can be dismissed early. */
  notify: (kind: ToastKind, message: string, detail?: string) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

/** How long each toast stays on screen. Errors linger so they can be read. */
const DURATIONS: Record<ToastKind, number> = {
  success: 3200,
  info: 3200,
  warning: 5000,
  error: 6000,
}

const ICONS: Record<ToastKind, string> = {
  success: 'M6.5 11.6 3.4 8.5l-.9.9 4 4 7-7-.9-.9z',
  error: 'M8 1.5A6.5 6.5 0 1 0 8 14.5 6.5 6.5 0 0 0 8 1.5zm.6 9.9H7.4v-1.2h1.2v1.2zm0-2.3H7.4V4.6h1.2v4.5z',
  warning: 'M8 1.8 15 14H1L8 1.8zm-.6 4.4v3.6h1.2V6.2H7.4zm0 4.6v1.2h1.2v-1.2H7.4z',
  info: 'M8 1.5A6.5 6.5 0 1 0 8 14.5 6.5 6.5 0 0 0 8 1.5zM7.4 4.6h1.2v1.2H7.4V4.6zm0 2.4h1.2v4.4H7.4V7z',
}

/**
 * Provides Office-style toast notifications. Wrap the task pane once, then call
 * `useToast().notify('success', 'Template inserted')` from anywhere below it.
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    setToasts(current => current.filter(t => t.id !== id))
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
  }, [])

  const notify = useCallback((kind: ToastKind, message: string, detail?: string) => {
    const id = nextId.current++
    setToasts(current => [...current, { id, kind, message, detail }])
    timers.current.set(id, setTimeout(() => dismiss(id), DURATIONS[kind]))
    return id
  }, [dismiss])

  // Clear outstanding timers if the pane unmounts mid-toast.
  useEffect(() => {
    const pending = timers.current
    return () => {
      pending.forEach(clearTimeout)
      pending.clear()
    }
  }, [])

  const value = useMemo(() => ({ toasts, notify, dismiss }), [toasts, notify, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

/** The stack of visible toasts. Rendered by ToastProvider; not needed directly. */
export const ToastViewport: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({
  toasts,
  onDismiss,
}) => {
  if (toasts.length === 0) return null

  return (
    <div className="toast-viewport" role="region" aria-label="Notifications">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`toast toast--${toast.kind}`}
          role={toast.kind === 'error' ? 'alert' : 'status'}
          aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
        >
          <svg className="toast-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d={ICONS[toast.kind]} />
          </svg>
          <div className="toast-body">
            <span className="toast-message">{toast.message}</span>
            {toast.detail && <span className="toast-detail">{toast.detail}</span>}
          </div>
          <button
            type="button"
            className="toast-dismiss"
            onClick={() => onDismiss(toast.id)}
            aria-label="Dismiss notification"
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M4.3 3.4 8 7.1l3.7-3.7.9.9L8.9 8l3.7 3.7-.9.9L8 8.9l-3.7 3.7-.9-.9L7.1 8 3.4 4.3z" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  )
}

/** Access the toast API. Throws outside a ToastProvider so the mistake is obvious. */
export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
